/**
 * Names to wording, for the findings the panels raise.
 *
 * `domain/` already owns two of these tables — `PROBLEM_MESSAGE_KEYS` for a row
 * that cannot be sent, and `CAMPAIGN_PROBLEM_MESSAGES` for a campaign that
 * cannot be — because both are reached on more than one side. These two are the
 * ones only a screen has ever needed: the two-blocks panel's findings and the
 * import panel's.
 *
 * **Level is part of the table, not of the finding.** Whether a reversed pair
 * of blocks is an error or a question is a decision about how to talk to
 * somebody, and `blocks-view.ts` deliberately does not make it — §59 is
 * explicit that the administrator sees the truth and decides, so a double paste
 * is a warning with a fix beside it rather than a refusal.
 *
 * `test/web-wording.test.ts` holds these against the server-rendered panel's
 * copies while both exist, so the two cannot drift.
 */

import type { FindingName } from '../../blocks-view.ts';
import type { ImportFindingName } from '../../import-view.ts';

export type Level = 'error' | 'warning';

export interface Wording {
  readonly message: string;
  readonly level: Level;
  /** A button beside the sentence, for a finding that can be acted on. */
  readonly action?: string;
}

export const BLOCK_WORDING: Readonly<Record<FindingName, Wording>> = {
  'one-block': { message: 'campaign.blocks.oneBlock', level: 'error' },
  'too-many-blocks': { message: 'campaign.blocks.tooManyBlocks', level: 'error' },
  'count-mismatch': { message: 'campaign.blocks.countMismatch', level: 'error' },
  // A question, not a fact — and the only finding in the panel with a fix
  // attached, because the fix is unambiguous once somebody agrees with it.
  'blocks-reversed': { message: 'campaign.blocks.reversed', level: 'warning', action: 'campaign.blocks.swap' },
  'both-blocks-phones': { message: 'campaign.blocks.bothPhones', level: 'warning' },
  'separator-in-lines': { message: 'campaign.blocks.separatorInLines', level: 'warning' },
};

/** Every way an import can be refused. All of them are refusals. */
export const IMPORT_WORDING: Readonly<Record<ImportFindingName, Wording>> = {
  'import-empty': { message: 'campaign.import.empty', level: 'error' },
  'import-single-column': { message: 'campaign.import.singleColumn', level: 'error' },
  'import-over-limit': { message: 'campaign.import.overLimit', level: 'error' },
  'import-not-a-zip': { message: 'campaign.import.notAZip', level: 'error' },
  'import-no-worksheet': { message: 'campaign.import.noWorksheet', level: 'error' },
  'import-unsupported-compression': { message: 'campaign.import.unsupportedCompression', level: 'error' },
};
